import Link from "next/link";
import React from "react";

export const TermsOfUse = (): React.ReactElement => (
  <div style={{maxWidth: 1200, margin: "20px auto 40px"}} className="tos ph16 pt24 pb16">
    <h1>Carbon Health Technologies Terms of Use</h1>
    <p>These Terms were last updated on Feb, 9 2021</p>
    <p>Agreement to Terms of Use</p>
    <p>
      These Carbon Health Technologies Terms of Use (“Terms” or “Agreement”) govern your use of our
      online interfaces and properties (e.g., websites and mobile applications) owned and controlled
      by Carbon Health Technologies, Inc., including the carbonhealth.com website and the Carbon
      Health Technologies mobile application (collectively, the “Site”), as well as the services
      (“Services”) available to users through the Site. Carbon Health Technologies, Inc. (“Carbon
      Health Tech,” “we,” “us,” and “our”) contracts with Carbon Health Medical Group of Florida,
      P.A., Carbon Health Medical Group, Inc., Direct Urgent Care, Inc., Carbon Health Medical Group
      of New Jersey, P.A., Carbon Health Medical Group of Kansas, P.A. and Djavaherian Medical
      Practice, PLLC (collectively, “Carbon Health Medical”) to assist them in the provision of
      online telehealth medical consultations and secure messaging between Carbon Health Medical
      physicians and other healthcare professionals (individually the “Provider” and collectively
      the “Providers”) and their patients. (See below for supplemental terms applicable to
      Providers.) The professional medical services (which are provided by Carbon Health Medical)
      and the non-clinical Site services (which are provided by Carbon Health Tech) are collectively
      referred to in this Terms as the “Services”. The terms “you” and “your” means you, your
      dependents if any, and any other person accessing your Carbon Health Technologies Account.
    </p>
    <p>
      <b>
        Your acceptance of, and compliance with, these Terms is a condition to your use of the Site
        and Services and purchase of Products. By clicking “accept” or otherwise using our Site and
        Services, you acknowledge that you have read, understand, and accept all terms and
        conditions contained within these Terms, the{" "}
        <Link href="/notice-of-privacy-practices">Notice of Privacy Practices</Link> provided to you
        by the Carbon Health Medical, and our <Link href="/privacy-policy">Privacy Policy</Link>. If
        you do not agree to be bound by these terms, you are not authorized to access or use this
        Site or Services; promptly exit this Site.
      </b>
    </p>
    <p>
      <b>Binding Arbitration.</b> These Terms provide that all disputes between you and Carbon
      Health Technologies that in any way relate to these Terms or your use of the Site will be
      resolved by BINDING ARBITRATION. ACCORDINGLY, YOU AGREE TO GIVE UP YOUR RIGHT TO GO TO COURT
      (INCLUDING IN A CLASS ACTION PROCEEDING) to assert or defend your rights under these Terms.
      Your rights will be determined by a NEUTRAL ARBITRATOR and NOT a judge or jury and your claims
      cannot be brought as a class action. Please review the Section below entitled{" "}
      <b>
        <u>Binding Individual Arbitration</u>
      </b>{" "}
      for the details regarding your agreement to arbitrate any disputes with Carbon Health.
    </p>
    <p>Changes to Terms</p>
    <p>
      We have the right to change or add to the terms of this Agreement at any time by posting the
      amended Terms on Carbon Health Technologies websites and/or mobile applications (collectively,
      the “Site”). In accordance with our{" "}
      <Link href="/notice-of-privacy-practices">Notice of Privacy Practices</Link> (“
      <Link href="/notice-of-privacy-practices">HIPAA Privacy Practices</Link>”, “
      <Link href="/notice-of-privacy-practices">Notice of HIPAA Privacy Practices</Link>
      ”), changes to these Terms will not weaken the privacy protections applied to your Protected
      Health Information without your being notified. Any use of the Services after our publication
      of any such changes shall constitute your acceptance of the Terms as modified. However, any
      Dispute that arose before the modification shall be governed by the Terms (including the
      binding individual arbitration clause) that was in place when the Dispute arose.
    </p>
    <p>
      <h3>
        **Emergencies** THIS SITE IS NOT AN EMERGENCY-RESPONSE OR EMERGENCY-MONITORING SERVICE AND
        ANY PERSON WHO IS AWARE OF AN EMERGENCY SITUATION OR BELIEVES THAT A PERSON MAY BE AT RISK
        OF INJURY OR DEATH OR WHO MAY HARM THEMSELVES OR ANOTHER PERSON SHOULD DIAL “911” OR AN
        APPROPRIATE EMERGENCY RESPONDER. CARBON HEALTH IS UNDER NO OBLIGATION TO MONITOR OR RESPOND
        TO COMMUNICATIONS MADE TO THIS SITE.
      </h3>
    </p>
    <h3>Services Provided – No Medical Care or Advice by Carbon Health</h3>
    <p>
      As an important note, there is no single provider of medical care called “Carbon Health” that
      provides clinical services online. Rather, Carbon Health Technologies offers an online
      communication platform for Providers and their patients to connect via the Site through the
      use of synchronous and asynchronous telecommunications technologies. The Site facilitates
      communication between patients and Providers.
    </p>
    <p>
      <b>
        These Terms do not define how we ensure our adherence to Federal and State laws regarding
        your Protected Health Information, including the Health Insurance and Portability Act of
        1996 (“HIPAA”). Our policies regarding the processing of your Protected Health Information
        (“PHI”) are covered in our{" "}
        <Link href="/notice-of-privacy-practices">Notice of Privacy Practices</Link> (“HIPAA Privacy
        Practices”). Our <Link href="/notice-of-privacy-practices">HIPAA Privacy Practices</Link>{" "}
        define how we preserve the privacy of your Protected Health Information, and you should
        refer to that document, not this one, regarding all processes associated with your
        healthcare records and other PHI.
      </b>
    </p>
    <p>
      Carbon Health Technologies does not provide medical advice or care through the Site or the
      Services. Carbon Health Technologies contracts with Carbon Health Medical, which are
      independent, physician-owned medical groups with a network of United States based Providers
      who provide clinical telehealth services. Carbon Health Technologies provides administrative
      and business support services to Carbon Health Medical. Carbon Health Medical Providers
      deliver clinical services via the Carbon Health Technologies platform to their patients.
      Carbon Health Technologies licenses the “Carbon Health MEDICAL GROUP” brand name to the
      independently owned and operated medical practices that use its platform and its
      administrative, business support, and other services. Providers are independently contracted
      or employed by the Carbon Health Medical. Providers are not contracted or employed by Carbon
      Health Technologies. The Providers, and not Carbon Health Technologies, are responsible for
      the quality and appropriateness of the care they render to you. Carbon Health Technologies and
      Carbon Health Medical do not provide any legal advice or representations in any way regarding
      any legal issues associated with advice, information, goods, or services offered by a
      Provider, including but not limited to any compliance obligations or steps necessary to comply
      with any state or federal laws and regulations.
    </p>
    <p>
      Each of the Carbon Health entities, their related sites, locations, and care providers follow
      the terms of our <Link href="/notice-of-privacy-practices">Notice of Privacy Practices</Link>{" "}
      (“
      <Link href="/notice-of-privacy-practices">HIPAA Privacy Practices</Link>”) and you may refer
      to that document to understand the protections applied to your Protected Healthcare
      Information.
    </p>
    <p>
      The Providers are independent of Carbon Health Technologies and are merely using the Site as a
      way to communicate with you. Any information or advice received from a Provider comes from
      them alone, and not from Carbon Health Technologies. Neither Carbon Health Technologies, nor
      any of its subsidiaries or affiliates or any third party who may promote the Site or Service
      or provide a link to the Service, shall be liable for any professional advice obtained from a
      Provider via the Site or Service, nor any information obtained on the Site. Carbon Health
      Technologies does not recommend or endorse any specific Providers, tests, physicians,
      medications, products, or procedures. You acknowledge that your reliance on any Providers or
      information delivered by the Providers via the Site or Service is solely at your own risk and
      you assume full responsibility for all risks associated herewith.
    </p>
    <p>
      Carbon Health Technologies does not make any representations or warranties about the training
      or skill of any Providers who deliver services via the Site or Service. You will be provided
      with available Providers based solely on the information you submit to the Site. You are
      ultimately responsible for choosing your particular Provider.
    </p>
    <p>
      The content of the Site and the Services, including without limitation, text, copy, audio,
      video, photographs, illustrations, graphics, and other visuals, is for informational purposes
      only and does not constitute professional medical advice, diagnosis, treatment, or
      recommendations of any kind by Carbon Health Technologies. You should always seek the advice
      of your qualified health care professionals with any questions or concerns you may have
      regarding your individual needs and any medical conditions. All information provided by Carbon
      Health Technologies, or in connection with any communications supported by Carbon Health
      Technologies, is intended to be for general information purposes only, and is in no way
      intended to create a provider-patient relationship as defined by state or federal law. While
      Carbon Health Technologies facilitates your selection of, and communications with, Providers,
      Carbon Health Technologies does not provide medical services, and the doctor-patient
      relationship is between you and the Carbon Health Medical Provider you select.
    </p>
    <p>
      The medical services, treatment, and care you receive may vary depending on the Provider you
      interact with. Please contact your Provider or Carbon Health Medical directly for any
      questions regarding your care or medical treatment.
    </p>
    <h3>Risks of Telehealth Services</h3>
    <p>
      Telehealth involves the delivery of healthcare services using electronic communications,
      information technology or other means between a healthcare provider and a patient who are not
      in the same physical location. While the provision of health care services using telehealth
      may offer certain potential benefits, there are also potential risks associated with the use
      of telehealth. The telehealth services are not a substitute for in-person health care in all
      cases. In order to use the Service, you will be required to review and agree to an informed
      consent regarding the use of telehealth (the “Patient Consent”) that will be provided to you
      by Carbon Health Medical and Providers through the Site. You agree that Carbon Health
      Technologies is a third-party beneficiary of the Patient Consent and has the right to enforce
      it against you.
    </p>
    <h3>Prescription Policy</h3>
    <p>
      Neither Carbon Health Technologies nor Carbon Health Medical endorse any specific medication,
      pharmacy, or pharmacologic product. If a Provider prescribes a medication, he/she will limit
      supply based upon state regulations and will only prescribe a medication as determined in
      his/her own discretion and professional judgment. There is no guarantee a prescription will be
      written. Providers do not prescribe DEA controlled substances or scheduled medications, or
      certain other drugs which may be harmful because of their potential for abuse. Providers
      reserve the right to deny care for actual or potential misuse of the Services.
    </p>
    <p>
      You agree that any prescriptions that you acquire from a Provider will be solely for your
      personal use. You agree to fully and carefully read all provided product information and
      labels and to contact a physician or pharmacist if you have any questions regarding the
      prescription. Carbon Health Technologies and Carbon Health Medical fully honor patient freedom
      of choice and, if you receive a prescription for a medication, you always have the option to
      instruct Carbon Health Medical to transmit that prescription to the pharmacy of your choice
    </p>
    <h3>Not an Insurance Product</h3>
    <p>
      Neither Carbon Health Technologies nor Carbon Health Medical are insurers. The Services are
      not insurance products, and the amounts you pay to Carbon Health Technologies or Carbon Health
      Medical are not insurance premiums. If you desire any type of health or other insurance, you
      will need to purchase such insurance separately.
    </p>
    <h4>Age Limitations</h4>
    <p>
      In order to access the Site and the Services, you represent and warrant that you are older
      than 18 years old. If you are under the age of eighteen (18) and wish to use our Site or
      Services, your parent or legal guardian must agree to your use of the Site and Services,
      submit your personal information, and agree to these Terms on your behalf. If you are under
      the age of 13, you may only use our Services and access our Site with the supervision and
      consent of your parents or legal guardians. If we learn that we have collected personal
      information from someone under the age of 13 without parental consent, we will promptly delete
      that information. If you believe we have improperly collected personal information from
      someone under the age of 13, please contact us at support@carbonhealth.com.
    </p>
    <h4>Restricted</h4>
    <p>
      You may use this Site only to the extent that you obey all laws, rules, and regulations
      applicable to your use of this Site. Carbon Health Technologies and Carbon Health Medical
      operate subject to state and federal laws and regulations, and the Services may not be
      available in your state. You represent that you are not a person barred from enrolling for or
      receiving the Services under the laws of the United States or other applicable jurisdictions
      in which you may be located. Access to and use of the Site or the Services is limited
      exclusively to users located in States within the United States where the Services are
      available. Accessing the Site or Services from jurisdictions where content is illegal, or
      where we do not offer Services, is prohibited.
    </p>
    <p>
      You are prohibited from violating or attempting to violate the security of the Site,
      including, without limitation: (a) accessing data not intended for such user or logging onto a
      server or an account which the user is not authorized to access; (b) attempting to probe,
      scan, or test the vulnerability of a system or network or to breach security or authentication
      measures without proper authorization; or (c) accessing or using the Site or any portion
      thereof without authorization, in violation of these Terms or in violation of applicable law.
    </p>
    <p>
      You may not use any scraper, crawler, spider, robot, or other automated means of any kind to
      access or copy data on the Site, deep-link to any feature or content on the Site, bypass our
      robot exclusion headers, or other measures we may use to prevent or restrict access to the
      Site. Violations of system or network security may result in civil or criminal liability.
      Carbon Health Technologies may investigate occurrences that may involve such violations and
      may involve, and cooperate with, law enforcement authorities in prosecuting users who are
      involved in such violations. You agree not to use any device, software, or routine to
      interfere or attempt to interfere with the proper working of this Site or any activity being
      conducted on this Site.
    </p>
    <h4>Your Privacy</h4>
    <p>
      Upon acceptance of these Terms you confirm that you have read, understood, and accepted Carbon
      Health Technologies’ Online <Link href="/privacy-policy">Privacy Policy</Link> and the{" "}
      <Link href="/notice-of-privacy-practices">Notice of Privacy Practices</Link> shared with
      Carbon Health Medical, as applicable.
    </p>
    <h4>Registration</h4>
    <p>
      Registration is not required to view certain content on the Site. However, to use some parts
      of the Site and Services you may be required to register and provide certain information about
      yourself, including your e-mail address and password (“Credentials”). If you become a
      registered member of the Site, you accept responsibility for all activities that occur under
      your registration account. You agree to provide true, accurate, complete, and correct
      information at the time of registration, and to promptly update this information as needed so
      that it remains true, accurate, complete, and correct. You should keep your Credentials
      private and not share your Credentials with anyone else. You are responsible for maintaining
      the confidentiality of your Credentials, and for all activities that occur under such
      Credentials. If you believe someone has accessed the Site using your Credentials without your
      authorization, e-mail us immediately at privacy@carbonhealth.com.
    </p>
    <h4>Transactions</h4>
    <p>
      If you wish to purchase products or services described or linked to on the Site (each such
      purchase, a “Transaction”), Carbon Health Technologies or the third party provider of the
      product or service will request certain information from you that is applicable to your
      Transaction, including, without limitation, credit card and other payment and shipping
      information. You understand that, if any such information is provided to Carbon Health
      Technologies, then Carbon Health Technologies shall treat any such information in the manner
      described in our Online Privacy Policy. By supplying such information, you grant Carbon Health
      Technologies the right to provide such information to third parties for purposes of
      facilitating the completion of Transactions initiated by you or on your behalf. You agree to
      pay all charges incurred by you or any users of your membership account or credit card (or
      other applicable payment mechanism) at the price(s) in effect when such charges are incurred,
      including, without limitation, all shipping and handling charges. You shall also be
      responsible for paying any applicable taxes relating to your purchases. YOU REPRESENT AND
      WARRANT THAT YOU HAVE THE LEGAL RIGHT TO USE ANY CREDIT CARD(S) OR OTHER PAYMENT MECHANISM
      USED IN CONNECTION WITH ANY TRANSACTION.
    </p>
    <p>
      Descriptions or images of, or references to, products or services on the Site do not imply
      Carbon Health Technologies’ endorsement of such products or services. We reserve the right,
      without prior notification, to change such descriptions or references, to limit the order
      quantity on any product or service and/or to refuse service to you. Verification of
      information applicable to a purchase may be required prior to Carbon Health Technologies’
      acceptance of any order. Price and availability of any product or service are subject to
      change without notice. Carbon Health Technologies is not responsible for errors in the prices
      or descriptions of any product or service. Refunds and exchanges shall be subject to Carbon
      Health Technologies’ and/or any applicable third party’s refund and exchange policies in
      effect at the time of the applicable Transaction. Current rates for any product or service
      available through the Site may be obtained by sending an email to{" "}
      <a href="mailto:support@carbonhealth.com">support@carbonhealth.com</a>.
    </p>
    <h4>Consent to Electronic Communications</h4>
    <p>
      You agree to receive email communications from Carbon Health Technologies, Inc. and Carbon
      Health Medical and request online access to your profile to do things such as make or cancel
      appointments. By providing your phone number to us, you grant permission for Carbon Health
      Technologies, Inc. and Carbon Health Medical to send you SMS text messages and emails
      regarding sign up, wait times, my appointments, follow up questions, billing questions,
      attempts to collect payment (should the need arise), and other Services related messages. .
    </p>
    <p>
      You grant and state your preference to have all staff at Carbon Health Technologies, Inc. and
      Carbon Health Medical communicate with you by email or standard SMS messaging regarding
      various aspects of my medical care, which may include, but shall not be limited to, test
      results, prescriptions, appointments, patient satisfaction surveys and billing. You understand
      that email and standard SMS messaging are not confidential methods of communication and may be
      insecure. You further understand that, because of this, there is a risk that email and
      standard SMS messaging regarding your medical care might be intercepted and read by a third
      party.
    </p>
    <ol>
      <li>
        When you opt-in to the service, we will send you an SMS message to confirm your signup.
      </li>
      <li>
        You can cancel the SMS service at any time. Just text "STOP" to us. After you send the SMS
        message "STOP" to us, we will send you an SMS message to confirm that you have been
        unsubscribed. After this, you will no longer receive SMS messages from us.
      </li>
      <li>
        If at any time you forget what keywords are supported, just text "HELP" to us. After you
        send the SMS message "HELP" to us, we will respond with instructions on how to use our
        service as well as how to unsubscribe.
      </li>
      <li>
        We are able to deliver messages to major mobile phone carriers by using Twilio’s services, a
        third party service provider. Please note that{" "}
        <b>carriers are not liable for delayed or undelivered messages.</b>
      </li>
      <li>
        As always, message and data rates may apply for any messages sent to you from us and to us
        from you. Message frequency varies. If you have any questions about your text plan or data
        plan, it is best to contact your wireless provider. For all questions about the services
        provided by this short code, you can send an email to{" "}
        <a href="mailto:support@carbonhealth.com">support@carbonhealth.com</a>.
      </li>
      <li>
        If you have any questions regarding privacy, please read our privacy policy:
        <Link href="/privacy-policy">/privacy-policy</Link>
      </li>
    </ol>
    <h4>Social Media and Online Communities; Other User Content</h4>
    <p>
      Carbon Health Technologies may provide you opportunities to share information on third party
      social media sites or platforms such as Facebook, Instagram, LinkedIn, Twitter, Google+,
      YouTube or other similar sites (collectively, “Social Media Sites”). Carbon Health
      Technologies may also provide you opportunities to participate in online communities on such
      Social Media Sites and may host discussion boards, chats, and other forums on this Site. For
      example, you may use your Instagram handle and tag your photos and postings (“Tagged Content”)
      with hashtags that we may provide from time to time in order to submit your Tagged Content for
      potential use on our Site. If you post Tagged Content with hashtags as we may provide from
      time to time, your activity and participation is governed by these Terms. Additionally, you
      remain responsible for your compliance with other applicable terms and conditions such as
      those of Instagram or other Social Media Sites. Without limitation of any other obligations,
      you agree that you will be respectful of others and their privacy and will not submit photos
      of others without their express permission.
    </p>
    <p>
      Carbon Health Technologies may terminate or restrict your access to any Carbon Health
      Technologies online community, including access through the Site.
    </p>
    <p>
      If you submit, upload, post, or transmit any Tagged Content, health information, medical
      history, conditions, problems, symptoms, personal information, consent forms, agreements,
      requests, comments, ideas, suggestions, information, files, videos, images, or other materials
      to us or our Site (“User Information”), you agree not to provide any User Information that (a)
      is false, inaccurate, defamatory, abusive, libelous, unlawful, obscene, threatening,
      harassing, fraudulent, pornographic, or harmful, or that could encourage criminal or unethical
      behavior, (b) violates or infringes the privacy, copyright, trademark, trade dress, trade
      secrets, or intellectual property rights of any person or entity, or (c) contains or transmits
      a virus or any other harmful component. You agree not to contact other Site users through
      unsolicited e-mail, telephone calls, mailings, or any other method of communication. You
      represent and warrant to Carbon Health Technologies and Carbon Health Medical that you have
      the legal right and authorization to provide all User Information to Carbon Health
      Technologies and Carbon Health Medical for use as set forth herein and required by Carbon
      Health Technologies and the Carbon Health Medical Provider.
    </p>
    <p>
      Except as protected or prohibited by applicable law, Carbon Health Technologies shall have a
      royalty-free, irrevocable, transferable right and license to use the Provider Content however
      Carbon Health Technologies desires, including without limitation, to copy, modify, delete in
      its entirety, adapt, publish, translate, create derivative works from or sell or distribute
      such Provider Content or incorporate such Provider Content into any form, medium, or
      technology throughout the world.{" "}
    </p>
    <p>
      <b>
        Our policies regarding the processing of your Protected Health Information (“PHI”) are
        covered in our <Link href="/notice-of-privacy-practices">Notice of Privacy Practices</Link>{" "}
        (“
        <Link href="/notice-of-privacy-practices">HIPAA Privacy Practices</Link>
        ”). Our <Link href="/notice-of-privacy-practices">HIPAA Privacy Practices</Link> define how
        we preserve the privacy of your Protected Health Information, and you should refer to that
        document, not this one, regarding all processes associated with your healthcare records and
        other PHI.
      </b>
    </p>
    <p>
      You agree not to: (i) access the Site or use the Services in any unlawful way or for any
      unlawful purpose; (ii) post or transmit (1) a message under a false name, or (2) any data,
      materials, content, or information (including, without limitation, advice, and
      recommendations) which is (A) libelous, defamatory, obscene, fraudulent, false, or contrary to
      the ownership or intellectual property rights of any other person, or (B) contains or promotes
      any virus, worm, Trojan horse, time bomb, malware, or other computer programing or code that
      is designed or intended to damage, destroy, intercept, download, interfere, manipulate, or
      otherwise interrupt or expropriate the Site or the Services, personal information, software,
      equipment, servers, or Information or facilitate or promote hacking or similar conduct; (iii)
      impersonate or misrepresent your identity or falsely state or misrepresent your affiliation
      with a person or entity; (iv) tamper, hack, spoof, copy, modify, or otherwise corrupt the
      administration, security, or proper function of the Site or the Services; (v) use robots or
      scripts with the Site; (vi) attempt to reverse engine, reverse assemble, reverse compile,
      decompile, disassemble, translate, or otherwise alter, defraud, or create false results from
      any executable code, information on, or received by this Site; (vii) to have any antivirus or
      antispyware software running that is set to override the internet browser’s cookies setting;
      (viii) incorrectly identify the sender of any message transmitted to Carbon Health; (ix) alter
      the attribution or origin of electronic mail, messages, or posting; (x) harvest or collect
      personal health information about any other individual who uses the Site or the Services; and
      (xi) infringe or facilitate infringement on any copyright, patent, trademark, trade secret, or
      other proprietary, publicity, or privacy rights of any party, including but not limited to,
      such rights of third parties.
    </p>
    <h4>Selection and Removal of Tagged Content</h4>
    <p>
      Carbon Health will review Tagged Content and select certain Tagged Content for posting on our
      Site; we make no guarantee that your Tagged Content will be posted. Notwithstanding the
      foregoing, you remain fully responsible and liable for your Tagged Content and its compliance
      with these Terms, other applicable Terms and applicable laws. If your Tagged Content is posted
      to our Site, you may request removal of your Tagged Content by marking it as “private” in your
      Social Media Site account or by emailing us at{" "}
      <a href="mailto:support@carbonhealth.com">support@carbonhealth.com</a>.
    </p>
    <h4>Monitoring</h4>
    <p>
      We have no obligation to monitor any related websites, chats, discussion boards or any other
      materials that you or third parties transmit or post on or to the Site or related websites, if
      any. However, you acknowledge and agree that we have the right (but not the obligation) to
      monitor the Site, discussion boards, the chats, and the materials you transmit or post; to
      alter or remove any such materials (including, without limitation, any posting to a chat or
      discussion board); and to disclose such materials and the circumstances surrounding their
      transmission to any third party in order to operate the Site properly, to protect ourselves,
      our sponsors, and our other clients and visitors, and to comply with legal obligations or
      governmental requests.
    </p>
    <h4>Ownership and IP Licensing</h4>
    <p>
      This Site is owned and operated by Carbon Health. The entire contents and design of the site
      are protected by U.S. and international copyright law. All rights regarding the Site and
      materials contained on the Site are either owned by Carbon Health, are licensed to it, or are
      used with permission. Carbon Health and its licensors retain and reserve all proprietary
      rights to the contents of this Site.
    </p>
    <p>
      Subject to these Terms and the payment of all applicable fees, Carbon Health grants you a
      revocable, nontransferable (except as provided below), personal, nonexclusive license to use
      the object code version of the Site. All rights not expressly granted to you in these Terms
      are reserved and retained by Carbon Health or its licensors, suppliers, publishers, rights
      holders, or other content providers. Neither the Site and Services, nor any part of the Site
      and Services, may be reproduced, duplicated, copied, sold, resold, visited, or otherwise
      exploited for any commercial purpose without express written consent of Carbon Health.
    </p>
    <p>
      You may not copy, republish, upload, post, display, transmit, or frame any of these materials
      without prior written consent from Carbon Health. You may link to, view, download, use,
      display and print a single copy of the materials found on this Site only for personal,
      noncommercial, and informational purposes as long as: (1) you do not alter or modify the
      materials in any way; (2) you include all applicable copyright, trademark and other notices
      and disclaimers; and (3) you do not use the materials in a way that suggests an association
      with Carbon Health or an affiliated entity. All such copies must include, at a minimum, the
      following copyright notice: “Copyright © [current year] Carbon Health All rights reserved.”
      Any other use of the Site or the information contained here is strictly prohibited. Carbon
      Health may terminate the above license at any time for any reason. If you breach any of these
      terms your license terminates immediately and automatically and without notice. Upon the
      termination of this license you must stop using this site, including all content, and return
      or destroy all copies, including electronic copies, of the content in your possession or
      control.
    </p>
    <h4>Copyright Infringement – DMCA Notice</h4>
    <p>
      The Digital Millennium Copyright Act (the “DMCA”) provides remedies for copyright holders who
      believe in good faith that material appearing on the Internet infringes their rights under
      copyright law. If you believe in good faith that content or material on this Site infringes a
      valid copyright owned by you, you (or your agent) may send Carbon Health a notice requesting
      that the material be removed, or access to it blocked. This request should be sent to:
      <a href="mailto:support@carbonhealth.com">support@carbonhealth.com</a>; or to: Carbon Health,
      300 California Street, 7th Floor, San Francisco, CA 94104.
    </p>
    <p>
      The notice must include the following information: (a) a physical or electronic signature of a
      person authorized to act on behalf of the owner of an exclusive right that is allegedly
      infringed; (b) identification of the copyrighted work claimed to have been infringed; (c)
      identification of the material that is claimed to be infringing or the subject of infringing
      activity; (d) the name, address, telephone number, and email address of the complaining party;
      (e) a statement that the complaining party has a good faith belief that use of the material in
      the manner complained of is not authorized by the copyright owner, its agent or the law; and
      (f) a statement that the information in the notification is accurate and, under penalty of
      perjury, that the complaining party is authorized to act on behalf of the owner of an
      exclusive right that is allegedly infringed. If you believe in good faith that a notice of
      copyright infringement has been wrongly filed against you, the DMCA permits you to send us a
      counter-notice. Notices and counter-notices must meet the then-current statutory requirements
      imposed by the DMCA. Notices and counter-notices with respect to the Site should be sent to
      the address above.
    </p>
    <h4>Trademark Notice</h4>
    <p>
      Carbon Health names and logos and all related product and service names, design marks, and
      slogans are the trademarks or service marks of Carbon Health. All rights are reserved. You are
      not authorized to use any Carbon Health name or mark in any advertisement, publicity or in any
      other commercial manner without prior written consent of Carbon Health. All other trademarks
      appearing on the Site are the property of their respective owners.
    </p>
    <h4>Security</h4>
    <p>
      We have implemented technical and organizational measures designed to secure your personal
      information from accidental loss and from unauthorized access, use, alteration or disclosure.
      However, we cannot guarantee that unauthorized third parties will never be able to defeat
      those measures or use your personal information for improper purposes. You acknowledge that
      you provide your personal information at your own risk.
    </p>
    <h4>Representation and Warranties</h4>
    <p>
      You represent and warrant to us that: (a) you are at least eighteen (18) years of age; and (b)
      your use of this Site will be in compliance with these Terms.
    </p>
    <h4>No Warranties</h4>
    <p>
      USE OF THIS SITE AND THE SERVICES IS AT YOUR OWN RISK. TO THE MAXIMUM EXTENT PERMITTED BY
      APPLICABLE LAW, THE SITE AND SERVICES ARE PROVIDED WITHOUT WARRANTIES OF ANY KIND, WHETHER
      EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY,
      FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. NO ADVICE OR INFORMATION, WHETHER ORAL
      OR WRITTEN, OBTAINED BY YOU FROM CARBON HEALTH, CARBON HEALTH MEDICAL, OR OTHERWISE THROUGH
      THIS SITE OR SERVICES WILL CREATE ANY WARRANTY NOT EXPRESSLY STATED HEREIN. WITHOUT LIMITING
      THE FOREGOING, CARBON HEALTH, ITS PROCESSORS, ITS PROVIDERS, ITS LICENSORS (AND THEIR
      RESPECTIVE SUBSIDIARIES, AFFILIATES, AGENTS, DIRECTORS, AND EMPLOYEES) DO NOT WARRANT THAT THE
      CONTENT IS ACCURATE, RELIABLE OR CORRECT; THAT THIS SITE WILL MEET YOUR REQUIREMENTS; THAT
      THIS SITE WILL BE AVAILABLE AT ANY PARTICULAR TIME OR LOCATION, UNINTERRUPTED OR SECURE; THAT
      ANY DEFECTS OR ERRORS WILL BE CORRECTED; OR THAT THIS SITE IS FREE OF VIRUSES OR OTHER HARMFUL
      COMPONENTS. ANY CONTENT DOWNLOADED OR OTHERWISE OBTAINED THROUGH THE USE OF THIS SITE IS
      DOWNLOADED AT YOUR OWN RISK AND YOU WILL BE SOLELY RESPONSIBLE FOR ANY DAMAGE TO YOUR PROPERTY
      OR LOSS OF DATA THAT RESULTS FROM SUCH DOWNLOAD.
    </p>
    <p>
      CARBON HEALTH DOES NOT WARRANT, ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY FOR ANY PRODUCT
      OR SERVICE ADVERTISED OR OFFERED BY A THIRD PARTY THROUGH THIS SITE OR ANY HYPERLINKED WEBSITE
      OR SERVICE, OR FEATURED IN ANY BANNER OR OTHER ADVERTISING, AND CARBON HEALTH WILL NOT BE A
      PARTY TO OR IN ANY WAY MONITOR ANY TRANSACTION BETWEEN YOU AND THIRD-PARTY PROVIDERS OF
      PRODUCTS OR SERVICES.
    </p>
    <h4>Indemnity</h4>
    <p>
      BY VISITING THIS SITE OR USING THE SERVICES, YOU AGREE TO INDEMNIFY, DEFEND, AND HOLD HARMLESS
      CARBON HEALTH, CARBON HEALTH MEDICAL, THE PROVIDERS AND ANY AFFILIATED CARBON HEALTH ENTITY,
      ITS OFFICERS, DIRECTORS, EMPLOYEES, CONTRACTORS, AGENTS, AND REPRESENTATIVES (COLLECTIVELY
      “CARBON HEALTH PARTIES”) FROM AND AGAINST ALL CLAIMS, ACTIONS, DEMANDS, LIABILITIES,
      JUDGMENTS, SETTLEMENTS, COSTS, LOSSES, DAMAGES, TAX ASSESSMENTS, PENALTIES, INTEREST AND
      EXPENSES (INCLUDING, WITHOUT LIMITATION, REASONABLE ATTORNEYS’ FEES) ARISING OUT OF ANY
      DIRECT, INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, EXEMPLARY, PUNITIVE OR ANY OTHER CLAIM
      YOU OR A THIRD PARTY MAY INCUR IN CONNECTION WITH YOUR USE OR MISUSE OF THIS SITE OR SERVICES,
      INCLUDING, WITHOUT LIMITATION, ANY CLAIMS FOR DEFAMATION, VIOLATION OF PUBLICITY OR PRIVACY,
      COPYRIGHT OR TRADEMARK INFRINGEMENT RESULTING FROM YOUR SUBMISSIONS OF ANY USER CONTENT,
      TAGGED CONTENT OR OTHER MATERIALS, ANY ECONOMIC HARM, LOST PROFITS, DAMAGES TO BUSINESS, DATA
      OR COMPUTER SYSTEMS, OR ANY DAMAGES RESULTING FROM RELIANCE ON ANY CONTENT OR RESULTING FROM
      ANY INTERRUPTIONS, WORK STOPPAGES, COMPUTER FAILURES, DELETION OF FILES, ERRORS, OMISSIONS,
      INACCURACIES, DEFECTS, VIRUSES, DELAYS OR MISTAKES OF ANY KIND, EVEN IF YOU HAVE PREVIOUSLY
      ADVISED CARBON HEALTH OF THE POSSIBILITY OF SUCH CLAIM.
    </p>
    <h4>Limitation of Liability and Damages</h4>
    <p>
      CARBON HEALTH SHALL NOT BE LIABLE FOR THE ACTS OR OMISSIONS OF CARBON HEALTH MEDICAL OR THE
      PROVIDERS. YOUR SOLE REMEDY FOR DISSATISFACTION WITH THE SITE AND ANY OF ITS SERVICE IS TO
      STOP USING THE SITE OR SERVICE. YOU AGREE THAT UNDER NO CIRCUMSTANCE SHALL ANY OF CARBON
      HEALTH PARTIES BE LIABLE FOR ANY DAMAGE RESULTING FROM YOUR USE OR INABILITY TO USE THIS SITE,
      THE SERVICES, OR THE MATERIALS ON THIS SITE. THIS PROTECTION COVERS CLAIMS BASED ON WARRANTY,
      CONTRACT, TORT, STRICT LIABILITY, AND ANY OTHER LEGAL THEORY. THIS PROTECTION COVERS ALL
      LOSSES AND CLAIMS OF ANY TYPE INCLUDING, WITHOUT LIMITATION, DIRECT OR INDIRECT, SPECIAL,
      INCIDENTAL, RELIANCE, CONSEQUENTIAL, EXEMPLARY, AND PUNITIVE DAMAGES, PERSONAL INJURY/WRONGFUL
      DEATH, LOST PROFITS, OR DAMAGES RESULTING FROM LOST DATA OR BUSINESS INTERRUPTION. TO THE
      EXTENT ANY ASPECTS OF THE FOREGOING LIMITATIONS OF LIABILITY ARE NOT ENFORCEABLE, THE MAXIMUM
      LIABILITY OF CARBON HEALTH TO YOU WITH RESPECT TO YOUR USE OF THIS SITE IS $50 (FIFTY
      DOLLARS). YOU HEREBY AGREE TO WAIVE, TO THE FULLEST EXTENT PERMITTED BY LAW, ALL LAWS THAT
      LIMIT THE EFFICACY OF SUCH INDEMNIFICATIONS OR RELEASES.
    </p>
    <h3>Supplemental Terms Applicable to Providers</h3>
    <p>
      These supplemental terms apply to Providers in addition to the other provisions of these
      Terms. In the event of a conflict between the supplemental terms and any other terms herein,
      the supplemental terms shall prevail.
    </p>
    <p>
      To be a healthcare provider using the Site (for purposes of this Section, “Provider”, or
      “you”) you must be a licensed physician, nurse practitioner, or healthcare professional
      contracted or employed by Carbon Health Medical, and must agree to comply with all laws,
      medical board rules, and other rules and regulations applicable to you as a Provider or
      otherwise. Your relationship with the Carbon Health users (including, but not limited to, your
      Carbon Health Medical patients) is directly between you and the patient. The patient will
      never have a physician-patient relationship with Carbon Health. Carbon Health does not
      practice medicine and offers no medical services. As set forth more fully below, Provider is
      solely responsible for all agreements, consents, notices, and other interactions with patients
      and other consumers. Without limiting the generality of the foregoing, Provider and Carbon
      Health Medical is solely responsible for all billings and collections from patients and other
      consumers, and Carbon Health shall have no liability whatsoever to Provider with respect to
      any amounts owed by any patient or other consumer to Provider.
    </p>
    <p>
      Carbon Health does not provide any medical advice, legal advice, or representations in any way
      regarding any legal or medical issues associated with Provider, goods, or services offered by
      Provider, including but not limited to any compliance obligations or steps necessary to comply
      with any state or federal laws and regulations. Provider should seek legal counsel regarding
      any legal and compliance issues, and should not rely on any materials or content associated
      with the Services in determining Provider’s compliance obligations under law. Provider and
      Carbon Health agree that Carbon Health is not providing to customers, patients, or anyone
      else, medical advice or legal advice. THE CARBON HEALTH SITE AND THE SERVICES ARE NOT MEANT TO
      SUBSTITUTE OR MODIFY YOUR PROFESSIONAL JUDGMENT IN ANY WAY.
    </p>
    <p>
      Provider will use the Site and Services only in accordance with applicable standards of good
      medical practice. While software products such as the Site and Services can facilitate and
      improve the quality of service that Provider can offer patients, many factors, including but
      not limited to the provider/patient relationship can affect a patient outcome, and with
      intricate and interdependent technologies and complex decision-making it is often difficult or
      impossible to accurately determine what the factors were and in what proportion they affected
      an outcome. Provider shall be solely responsible for its use of the Site and Services, and the
      provision of medical services to Provider’s patients. In this regard, Provider releases Carbon
      Health and waives any and all potential claims against Carbon Health as a result of Provider’s
      use of the Site and Services, and the provision of services to Provider’s patients.
    </p>
    <p>
      As a result of the complexities and uncertainties inherent in the patient care process,
      Provider agrees to defend, indemnify and hold Carbon Health harmless from any claim by or on
      behalf of any patient of Provider, or by or on behalf of any other third party or person
      claiming damage by virtue of a familial or financial relationship with such a patient, which
      is brought against Carbon Health, regardless of the cause if such claim arises for any reason
      whatsoever, out of Provider’s use or operation of the Site and Services. To the extent
      applicable, Provider will obtain Carbon Health’s prior written consent to any settlement or
      judgment in which Provider agrees to any finding of fault of Carbon Health or defect in the
      Site or Services. Carbon Health will promptly notify Provider in writing of any claim subject
      to this indemnification, promptly provide Provider with the information reasonably required
      for the defense of the same, and grant to Provider exclusive control over its defense and
      settlement.
    </p>
    <p>
      If you submit, upload, transmit, or post any consents, notices, advice, recommendations,
      comments, files, videos, images, or other materials to us or our Site (“End User Content”) or
      provide any End User Content to patients or other consumers, you agree not to provide any End
      User Content that (a) is defamatory, abusive, libelous, unlawful, obscene, threatening,
      harassing, fraudulent, pornographic, or harmful, or that could encourage criminal or unethical
      behavior, (b) violates or infringes the privacy, copyright, trademark, trade dress, trade
      secrets, or intellectual property rights of any person or entity, or (c) contains or transmits
      a virus or any other harmful component. End User is solely responsible for obtaining all
      necessary agreements and consents from, and providing all required notices to, patients and
      other consumers. You agree not to contact other users through unsolicited e-mail, telephone
      calls, mailings, or any other method of communication. You represent and warrant to Carbon
      Health Technologies that you have the legal right and authorization to upload all End User
      Content at the Site. Carbon Health shall have a royalty-free, irrevocable, transferable right
      and license to use the End User Content however Carbon Health desires, including without
      limitation, to copy, modify, delete in its entirety, adapt, publish, translate, create
      derivative works from or sell or distribute such End User Content or incorporate such End User
      Content into any form, medium, or technology throughout the world. Carbon Health Technologies
      is and shall be under no obligation: (i) to maintain any End User Content in confidence; (ii)
      to pay to you any compensation for any End User Content; or (iii) to respond to any End User
      Content.
    </p>
    <p>
      Carbon Health Technologies does not regularly review End User Content but does reserve the
      right (but not the obligation) to monitor and edit or remove any End User Content submitted to
      the Site. You grant Carbon Health the right to use the name that you submit in connection with
      any End User Content. You agree not to use a false email address, impersonate any person or
      entity, or otherwise mislead as to the origin of any End User Content. You are and shall
      remain solely responsible for the content of any End User Content you post to the Site or
      provide to patients or other consumers. Carbon Health Technologies and its affiliates take no
      responsibility and assume no liability for any End User Content submitted by you or any third
      party.
    </p>
    <h4>Disputes</h4>
    <p>
      If a dispute of any kind arises, we want to understand and address your concerns quickly and
      to your satisfaction. Please contact{" "}
      <a href="mailto:privacy@carbonhealth.com">privacy@carbonhealth.com</a> with any dispute. If we
      cannot resolve your concerns, we agree to an informal and inexpensive dispute resolution
      process requiring individual arbitration. “Disputes” between you and Carbon Health
      Technologies, including its processors, suppliers or licensors or their respective affiliates,
      agents, directors or employees, are defined for the purposes of these Terms to include any
      claim, controversy, or dispute (whether involving contract, tort, equitable, statutory, or any
      other legal theory) between you and Carbon Health Technologies including, but not limited to,
      any claims relating in any way to these Terms (including its breach, termination, or
      interpretation), any other aspect of our relationship, Carbon Health Technologies advertising,
      and any use of Carbon Health Technologies services.
    </p>
    <h4>Binding Individual Arbitration</h4>
    <p>
      You and Carbon Health Technologies agree to arbitrate all Disputes. Arbitration is more
      informal than a lawsuit in court and seeks to resolve disputes more quickly. Instead of a
      judge or a jury, the case will be decided by a neutral arbitrator who has the power to award
      the same damages and relief that a court can. ANY ARBITRATION UNDER THIS AGREEMENT WILL BE
      ONLY BE ON A INDIVIDUAL BASIS; CLASS ARBITRATIONS, CLASS ACTIONS, PRIVATE ATTORNEY GENERAL
      ACTIONS, AND CONSOLIDATION WITH OTHER ARBITRATIONS ARE NOT PERMITTED, AND YOU ARE WAIVING YOUR
      RIGHTS TO HAVE YOUR CASE DECIDED BY A JURY AND TO PARTICIPATE IN A CLASS ACTION AGAINST CARBON
      HEALTH. If any provision of this arbitration agreement is found unenforceable, the
      unenforceable provision shall be severed, and the remaining arbitration terms shall be
      enforced (but in no case shall there be a class arbitration). All Disputes shall be resolved
      finally and exclusively by binding individual arbitration with a single arbitrator
      administered by ADR Services, Inc. according to this provision and the applicable arbitration
      rules for that forum. Consumer claimants (individuals whose transaction is intended for
      personal, family, or household use) may elect to pursue their claims in their local
      small-claims court rather than through arbitration. The Federal Arbitration Act, 9 U.S.C. §§
      1-16, fully applies. If you are a consumer bringing a claim relating to personal, household,
      or family use, any arbitration hearing will occur within the county or parish where you
      reside. Otherwise, any arbitration hearing will occur in San Francisco, California, or another
      mutually agreeable location, or a location ordered by the arbitrator. The arbitrator’s award
      shall be binding on the parties and may be entered as a judgment in any court of competent
      jurisdiction. If you prevail on any claim for which you are legally entitled to attorney’s
      fees, you may recover those fees from the arbitrator. For any claim where you are seeking
      relief, Carbon Health Technologies will not seek to have you pay its attorney’s fees, even if
      fees might otherwise be awarded, unless the arbitrator determines that your claim was
      frivolous. For purposes of this arbitration provision, references to you and Carbon Health
      Technologies also include respective subsidiaries, affiliates, agents, employees,
      predecessors, successors, and assigns. Subject to and without waiver of the arbitration
      provisions above, you agree that any judicial proceedings (other than small claims actions in
      consumer cases) will be brought in and you hereby consent to the exclusive jurisdiction and
      venue in the state courts in San Francisco, California, or federal court for the Central
      District of California.
    </p>
    <h4>Jurisdiction</h4>
    <p>
      Information provided on Carbon Health Technologies’ Site is not targeted to users in any
      particular locality nor is it intended to constitute the doing of business in any
      jurisdiction.
    </p>
    <p>
      This Site is a service provided by Carbon Health Technologies and does not constitute any
      contact with any jurisdiction outside the State of California. Use of this Site is prohibited
      in any jurisdiction having laws that would void this Agreement in whole or essential part or
      which makes accessing the Site illegal. Users in such jurisdictions visit and use this Site
      entirely at their own risk. Note: the essential parts of this Agreement include, without
      limitation, the exclusive venue and exclusive remedy provisions and the warranty disclaimers.
    </p>
    <p>
      This Agreement is entered into and performed in the State of California, United States of
      America. It is governed by and shall be construed under the laws of California, exclusive of
      any choice of law or conflict of law provisions.
    </p>
    <h4>Requests for Information</h4>
    <p>
      If you contact Carbon Health Technologies and request information about our programs or
      services, we will use your email or postal address to provide the information you requested.
      You agree that we may use and share information you provide us as described in our Online
      Privacy Policy.
    </p>
    <h4>Limitation on Time to Initiate a Dispute</h4>
    <p>
      Unless otherwise required by law, an action or proceeding by you relating to any Dispute must
      commence within one year after the cause of action accrues.
    </p>
    <h4>Links to Other Websites</h4>
    <p>
      Carbon Health Technologies’ Site may contain links to third party websites as a convenience to
      you. The inclusion of any website link does imply an approval, endorsement, or recommendation
      by Carbon Health Technologies. You agree that you access any such website at your own risk,
      and that the site is not governed by the terms and conditions contained in these Terms. Carbon
      Health Technologies expressly disclaims any liability for these websites. Please remember that
      when you use a link to go from our Site to another website, our Online Privacy Policy and
      Notice of Privacy Practices is no longer in effect. Your browsing and interaction on any other
      website, including those that have a link on our Site, is subject to that website’s own rules
      and policies.
    </p>
    <h4>Downloadable Files and Email</h4>
    <p>
      Carbon Health Technologies cannot and does not guarantee or warrant that email or files
      available for downloading from its Site will be free of viruses or other code that may
      contaminate or destroy data on your computer. You are responsible for implementing sufficient
      protective procedures and checks to maintain the accuracy of your data for maintaining a data
      back-up or other means for the reconstruction of any lost data. Carbon Health Technologies
      does not assume any responsibility or risk for damage to your computer or its files related to
      your use of the Services.
    </p>
    <h4>Notice for California Users</h4>
    <p>
      If you have a question or complaint regarding the Services provided to you by Carbon Health
      please contact us at <a href="mailto:support@carbonhealth.com">support@carbonhealth.com</a> to
      receive further information regarding the Services or to resolve the complaint. You may also
      contact the Complaint Assistance Unit of the Division of Consumer Services of the Department
      of Consumer Affairs by telephone at (800) 952-5210 or TDD (800) 326-2297, or in writing at
      Department of Consumer Affairs, Consumer Information Division, 1625 North Market Blvd., Suite
      N 112, Sacramento, CA 95834.
    </p>
    <h4>Other Provisions</h4>
    <p>
      Except as expressly provided in these Terms, these terms are a complete statement of the
      agreement between you and Carbon Health Technologies, and they describe the entire liability
      of Carbon Health Technologies and its vendors and suppliers and your exclusive remedy with
      respect to your access and use of this Site. In the event of a conflict between these Terms
      and any other Carbon Health Technologies agreement or policy, these Terms shall prevail on the
      subject matter of these Terms. If any provision of these Terms is invalid or unenforceable
      under applicable law, then it shall be changed and interpreted to accomplish the objectives of
      such provision to the greatest extent possible under applicable law, and the remaining
      provisions will continue in full force and effect. Headings are included for convenience only,
      and shall not be considered in interpreting these Terms. These Terms do not limit any rights
      that Carbon Health Technologies may have under trade secret, copyright, patent, or other laws.
      Carbon Health Technologies’ failure to assert any right or provision under these Terms shall
      not constitute a waiver of such right or provision. No waiver of any term of this Agreement
      shall be deemed a further or continuing waiver of such term or any other term.
    </p>
    <h3>Force Majeure</h3>
    <p>
      We will not be deemed to be in breach of these terms or liable for any breach of these Terms
      or our privacy policy due to any event or occurrence beyond our reasonable control, including
      without limitation, acts of God, pandemic, terrorism, war, invasion, failures of any public
      networks, electrical shortages, earthquakes or floods, civil disorder, strikes, fire, or other
      disaster.
    </p>
    <h3>Support; Functionality</h3>
    <p>
      All questions and requests relating to Site support must be directed to Carbon Health
      Technologies. The Select Third Parties, as defined below, are not responsible for providing
      support for the application portions of the Site and may not be contacted for support. Carbon
      Health Technologies will use commercially reasonable efforts to respond to questions and
      provide support. Please note that we may change or remove functionality and other features of
      the Site at any time, without notice.
    </p>
    <h3>Modified Devices and Operating Systems</h3>
    <p>
      Carbon Health Technologies will have no liability for errors, unreliable operation, or other
      issues resulting from use of the Site on or in connection with rooted or jail broken devices
      or use on any mobile device that is not in conformance with the manufacturer’s original
      specifications, including, but not limited to, use of modified versions of the operating
      system (collectively, “Modified Devices”). Use of the Site on Modified Devices will be at your
      sole and exclusive risk and liability.
    </p>
    <h3>No Liability for Select Third Parties</h3>
    <p>
      Your wireless carrier, the manufacturer and retailer of your mobile device, the developer of
      the operating system for your mobile device, the operator of any application store,
      marketplace, or similar service through which you obtain the Site, and their respective
      affiliates, suppliers, and licensors (collectively, the “Select Third Parties”) are not
      parties to these Terms and they do not own and are not responsible for the Site. Carbon Health
      Technologies, and not any Select Third Parties, is responsible for addressing any claims
      raised by you or any third party regarding the Site or your use or possession thereof,
      including, but not limited to, claims related to product liability, legal, or regulatory
      requirements, and consumer protection or similar legislation. You are responsible for
      complying with all application store and other applicable Select Third Parties’ terms and
      conditions. YOU AGREE: (A) THE SELECT THIRD PARTIES DISCLAIM ALL WARRANTIES, EXPRESS AND
      IMPLIED, WITH RESPECT TO THE SITE, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF
      NON-INFRINGEMENT, TITLE, MERCHANTABILITY, QUIET ENJOYMENT, QUALITY OF INFORMATION, AND FITNESS
      FOR A PARTICULAR PURPOSE; (B) IN NO EVENT WILL THE SELECT THIRD PARTIES BE LIABLE TO YOU OR
      ANY THIRD PARTY FOR ANY DIRECT, INDIRECT, PUNITIVE, EXEMPLARY, INCIDENTAL, SPECIAL, OR
      CONSEQUENTIAL DAMAGES (WHETHER IN CONTRACT, TORT (INCLUDING NEGLIGENCE), OR OTHERWISE) ARISING
      OUT OF THIS AGREEMENT OR THE SITE, EVEN IF THEY HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH
      DAMAGES OR LOSSES; AND (C) IN ANY EVENT, THE MAXIMUM LIABILITY OF ANY SELECT THIRD PARTY FOR
      ALL CLAIMS (WHETHER IN CONTRACT, TORT (INCLUDING NEGLIGENCE), OR OTHERWISE) OF EVERY KIND WILL
      IN NOT EXCEED FIVE DOLLARS ($5.00); AND (IV) YOU WAIVE ANY AND ALL CLAIMS, NOW KNOWN OR LATER
      DISCOVERED, THAT YOU MAY HAVE AGAINST THE SELECT THIRD PARTIES ARISING OUT OF THE SITE AND
      THESE TERMS. THE THIRD PARTIES ARE INTENDED THIRD PARTY BENEFICIARIES OF THESE TERMS, AND ARE
      CAPABLE OF DIRECTLY ENFORCING ITS TERMS. NOTHING CONTAINED IN THESE TERMS WILL BE CONSTRUED AS
      MODIFYING OR AMENDING ANY AGREEMENTS OR OTHER TERMS BETWEEN YOU AND THE SELECT THIRD PARTIES
      WITH REGARD TO THEIR SUBJECT MATTER. In the event of any claim that the Site or your
      possession and use of the Site infringes a third party’s intellectual property rights, the
      Select Third Parties are not responsible for the investigation, defense, settlement, or
      discharge of the infringement claim.
    </p>
    <h4>Assignment</h4>
    <p>
      You may not assign any rights or obligations under this Agreement without Carbon Health
      Technologies’ prior written consent. Carbon Health Technologies may assign all or part of this
      Agreement.
    </p>
    <h4>Survival</h4>
    <p>
      All sections of this Agreement which, by their nature are designed to survive expiration or
      termination of this Agreement, including but not limited to indemnity and limitation of
      liability clauses, shall survive.
    </p>
    <h4>Waiver</h4>
    <p>
      No waiver of any of these terms shall be deemed a further or continuing waiver of such term or
      condition or any other term or condition.
    </p>
    <h4>Notices</h4>
    <p>
      You agree that we may provide any and all notices to you by e-mail, telephone, fax, as well as
      by any other method.
    </p>
    <h4>Severability</h4>
    <p>
      If any of the provisions of this Agreement are held unenforceable by a court or other tribunal
      of competent jurisdiction, then those provisions shall be limited or eliminated to the minimum
      extent necessary to allow the remainder of this Agreement to retain its full force and effect.
    </p>
    <h4>Entire Agreement; Amendment</h4>
    <p>
      This Agreement constitutes the entire agreement between you and Carbon Health Technologies
      applicable to its subject matter. It may not be modified except as described elsewhere in this
      Agreement.
    </p>
    <h4>Conflicting Terms</h4>
    <p>
      Anything on the Site inconsistent or in conflict with the terms of this Agreement is
      superseded by the terms of this Agreement.
    </p>
    <h4>Contact Information</h4>
    <p>Please contact us with any questions or concerns regarding this Agreement at:</p>
    <h4>Carbon Health Technologies, Inc.</h4>
    <p>
      300 California Street, 7th Floor San Francisco, CA 94104 Phone: 1-415-612-3256 Email:
      <a href="mailto:support@carbonhealth.com">support@carbonhealth.com</a>
    </p>
  </div>
);
